import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type BreadcrumbItem = {
  label: string;
  to?: string;
};

type BreadcrumbContextValue = {
  pageCrumbs: BreadcrumbItem[];
  setPageCrumbs: (crumbs: BreadcrumbItem[]) => void;
  clearPageCrumbs: () => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined);

export function useBreadcrumbs(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return ctx;
}

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [pageCrumbs, setPageCrumbsState] = useState<BreadcrumbItem[]>([]);

  // When route changes, clear any page-specific crumbs.
  useEffect(() => {
    setPageCrumbsState([]);
  }, [location.pathname]);

  const setPageCrumbs = useCallback((crumbs: BreadcrumbItem[]) => {
    setPageCrumbsState(Array.isArray(crumbs) ? crumbs : []);
  }, []);

  const clearPageCrumbs = useCallback(() => {
    setPageCrumbsState([]);
  }, []);

  const value = useMemo(
    () => ({
      pageCrumbs,
      setPageCrumbs,
      clearPageCrumbs,
    }),
    [pageCrumbs, setPageCrumbs, clearPageCrumbs]
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}
